import { useLayoutEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { TrackedRect } from "@/lib/guide/useTrackedRect";
import type { GuideCaptionSide } from "@/lib/guide/types";

const GAP = 10;
const MARGIN = 8;
const FALLBACK_W = 260;

const DEFAULT_ORDER: GuideCaptionSide[] = ["below", "above", "right", "left"];

/**
 * Pick where the caption sits relative to the target: the roomiest of four
 * sides, biased to `preferred` when set. Pure arithmetic on the target rect +
 * the caption's own measured size — no per-frame DOM work, so it stays cheap on
 * slow machines (the flip logic the buttons already use, one level richer).
 */
function placeCaption(
  rect: TrackedRect,
  w: number,
  h: number,
  vw: number,
  vh: number,
  preferred?: GuideCaptionSide,
): { x: number; y: number } {
  const clampX = (x: number) =>
    Math.min(Math.max(MARGIN, x), Math.max(MARGIN, vw - w - MARGIN));
  const clampY = (y: number) =>
    Math.min(Math.max(MARGIN, y), Math.max(MARGIN, vh - h - MARGIN));
  const centerX = rect.left + rect.width / 2 - w / 2;
  const centerY = rect.top + rect.height / 2 - h / 2;

  // `room` = how much the caption clears (≥0) or misses by (<0) on that side.
  const bySide: Record<
    GuideCaptionSide,
    { x: number; y: number; room: number }
  > = {
    below: {
      x: centerX,
      y: rect.top + rect.height + GAP,
      room: vh - rect.top - rect.height - (h + GAP + MARGIN),
    },
    above: {
      x: centerX,
      y: rect.top - h - GAP,
      room: rect.top - (h + GAP + MARGIN),
    },
    right: {
      x: rect.left + rect.width + GAP,
      y: centerY,
      room: vw - rect.left - rect.width - (w + GAP + MARGIN),
    },
    left: {
      x: rect.left - w - GAP,
      y: centerY,
      room: rect.left - (w + GAP + MARGIN),
    },
  };

  // Try the preferred side first, then the default order; first that fits wins,
  // else the side that misses by the least.
  const order = preferred
    ? [preferred, ...DEFAULT_ORDER.filter((s) => s !== preferred)]
    : DEFAULT_ORDER;
  const fitting = order.find((s) => bySide[s].room >= 0);
  // Roomiest side as the fallback. `order` is always non-empty, but seed the
  // reduce with its first element anyway (Sonar S6959 / safe on an empty array).
  const chosen =
    fitting ??
    order.reduce((a, b) => (bySide[b].room > bySide[a].room ? b : a), order[0]);

  const p = bySide[chosen];
  return { x: clampX(p.x), y: clampY(p.y) };
}

/** Copy uses a literal `/newline` marker to force a line break; turn each into
 *  a real break (trimming stray spaces around it) so the caption wraps where
 *  the author intended. Shared with the fixed advance panel. */
export function toCaptionLines(text: string): string {
  return text
    .split("/newline")
    .map((part) => part.trim())
    .filter(Boolean)
    .join("\n");
}

/**
 * Minimal one-line caption anchored to the highlighted element — the whole
 * on-screen guide chrome for a target step. The ring shows WHERE, the caption
 * says WHAT. For steps with a natural action the user just does it and the
 * guide follows; the caption is `pointer-events-none` so it never intercepts a
 * click or dismisses a popover. For a purely explanatory step (`onNext` set)
 * it also renders a small "Got it" button — the ONLY interactive part, so just
 * that button re-enables pointer events. Positioned with the house flip/clamp
 * pattern.
 */
export function GuideCaption({
  rect,
  text,
  onNext,
  nextLabel,
  onClose,
  preferredSide,
}: Readonly<{
  rect: TrackedRect;
  text: string;
  onNext?: () => void;
  nextLabel?: string;
  /** Explicit dismiss (× in the corner). Needed since clicking away no longer
   *  ends the guide. */
  onClose?: () => void;
  /** Bias the caption to a side; unset = auto (roomiest). */
  preferredSide?: GuideCaptionSide;
}>) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);

  useLayoutEffect(() => {
    const w = ref.current?.offsetWidth ?? FALLBACK_W;
    const h = ref.current?.offsetHeight ?? 40;
    setPos(
      placeCaption(
        rect,
        w,
        h,
        window.innerWidth,
        window.innerHeight,
        preferredSide,
      ),
    );
  }, [rect, text, onNext, preferredSide]);

  return (
    <div
      ref={ref}
      data-testid="guide-caption"
      role="status"
      className="fixed z-[10060] flex max-w-[280px] flex-col items-center gap-1.5 rounded-lg bg-guide px-3 py-2 text-center text-sm font-medium text-guide-foreground shadow-lg pointer-events-none select-none"
      style={{
        left: pos?.x ?? 0,
        top: pos?.y ?? 0,
        visibility: pos ? "visible" : "hidden",
      }}
    >
      {onClose && (
        <button
          type="button"
          data-testid="guide-caption-close"
          aria-label={t("guide.close")}
          onClick={onClose}
          className="pointer-events-auto absolute -right-2 -top-2 rounded-full bg-guide p-0.5 text-guide-foreground shadow hover:text-guide-foreground"
        >
          <X className="h-3 w-3" />
        </button>
      )}
      <span className="whitespace-pre-line">{toCaptionLines(text)}</span>
      {onNext && (
        <button
          type="button"
          data-testid="guide-caption-next"
          onClick={onNext}
          className="pointer-events-auto self-end rounded-md bg-guide-foreground/20 px-2 py-0.5 text-xs font-semibold hover:bg-guide-foreground/30"
        >
          {nextLabel}
        </button>
      )}
    </div>
  );
}
